import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import logo from '../../assets/icons/logo_navbar_w.svg'
import { Link } from 'react-router-dom';
import { OrgTypes } from 'utils/orgTypes';
import { useSession } from 'contexts';

// Expected Data Structure
/* 
props: {
  isOpen: bool,                                                                         // open/closed state
  data: {
    title: string,                                                                      // name of window
    content: JSX                                                                        // component from components/Flyout/Content
  },
}
*/

export interface FlyoutProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: {
    title: JSX.Element | string;
    content: JSX.Element;
    icon?: JSX.Element;
  };
}

export interface FlyoutHookReturn {
  flyoutProps: FlyoutProps;
  activeFlyout: FlyoutProps['data'];
  setActiveFlyout: React.Dispatch<React.SetStateAction<FlyoutProps['data']>>;
  updateFlyout: (data?: React.SetStateAction<FlyoutProps['data']>) => void;
  closeFlyout: (data?: React.SetStateAction<FlyoutProps['data']>) => void;
}

export const useFlyout = ({ isOpen: isOpenInit = false, data: { title = '', content = <></> } = {} } = {}): FlyoutHookReturn => {
  const [isOpen, setIsOpen] = useState(isOpenInit);
  const [activeFlyout, setActiveFlyout] = useState<FlyoutProps['data']>({ title: title, content: content });

  const updateFlyout = useCallback((data?: React.SetStateAction<FlyoutProps['data']>) => {
    if (data) setActiveFlyout(data);
    setIsOpen(true);
  }, []);

  const closeFlyout = useCallback((data?: React.SetStateAction<FlyoutProps['data']>) => {
    if (data) setActiveFlyout(data);
    setIsOpen(false);
  }, []);

  return {
    flyoutProps: { isOpen, setIsOpen, data: activeFlyout },
    activeFlyout,
    setActiveFlyout,
    updateFlyout,
    closeFlyout,
  };
};

export function Flyout({ setIsOpen, ...props }: FlyoutProps) {
  let content = props.isOpen ? props.data.content : <></>;
  const { organizationType } = useSession();

  const handleEscape = useCallback(
    function (e) {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [setIsOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape, true);
  }, [handleEscape]);

  return (
    <div>
      <div
        className={`${
          props.isOpen ? '' : 'translate-y-full'
        } w-full z-[59] transition-all ease-in-out fixed -mt-4 right-0 bottom-0 h-screen bg-[rgba(17,29,65,.99)] backdrop-blur-[10px]`}>
        <div className="shadow-2xl shadow-brand-dark/40 h-full overflow-auto">
          <div className="sticky top-0 z-10 w-full p-6 flex text-brand items-center justify-between bg-brand-dark">
            {/* <div className="flex items-center">
              {props.data.icon}
              <span className="text-lg font-bold">{props.data.title}</span>
            </div> */}
            <Link
              to={organizationType === OrgTypes.PART145 ? '/mro/dashboard' : '/crafts'}
              className="h-10">
              <img src={logo} className="h-10" alt="Logo" />
            </Link>
            <XMarkIcon className="h-6 w-6 cursor-pointer text-white" onClick={() => setIsOpen(false)} />
          </div>
          {content}
        </div>
      </div>
      {props.isOpen && (
        <div className="flex items-center justify-center fixed h-screen w-screen top-0 left-0 z-40">
          <div
            onClick={() => {
              setIsOpen(false);
            }}
            className="bg-brand-dark/40 backdrop-blur-[2px] w-full h-full absolute top-0 left-0 transition-all"></div>
        </div>
      )}
    </div>
  );
}

export default Flyout;
